import { FC } from 'react';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiPatient from 'hooks/useRoiPatient';
import PeriodComparison from '../PeriodComparison/PeriodComparison';

interface PatientsCardProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const PatientsCard: FC<PatientsCardProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data, isLoading, isValidating } = useRoiPatient({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const total = data?.periodData?.totalPatients ?? 0;
  const totalNew = data?.periodData?.totalNewPatients ?? 0;
  const totalExisting = data?.periodData?.totalExistingPatients ?? 0;

  const lastTotal = data?.lastPeriodData?.totalPatients ?? 0;
  const lastNew = data?.lastPeriodData?.totalNewPatients ?? 0;
  const lastExisting = data?.lastPeriodData?.totalExistingPatients ?? 0;

  const newPct = total > 0 ? (totalNew / total) * 100 : 0;
  const existingPct = total > 0 ? (totalExisting / total) * 100 : 0;

  if (isLoading || isValidating) {
    return (
      <div className="px-2.4 py-2.4 shadow-elevation-06-hover bg-white h-full flex items-center justify-center">
        <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
      </div>
    );
  }

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white h-full flex flex-col justify-between">
      <div className="flex flex-col gap-y-[20.5px]">
        <span className="text-18 leading-[2.7rem] font-bold text-darkest-grey">
          Patients
        </span>
        <div className="flex justify-between items-center">
          <span className="text-30 font-bold text-magenta">
            {total.toLocaleString()}
          </span>
          <PeriodComparison current={total} last={lastTotal} />
        </div>
      </div>

      <div className="flex h-0.6 rounded-full overflow-hidden mt-1.6 mb-1.2">
        {total > 0 ? (
          <>
            <div className="bg-magenta" style={{ width: `${newPct}%` }} />
            <div
              className="bg-[#D8B4FE]"
              style={{ width: `${existingPct}%` }}
            />
          </>
        ) : (
          <div className="bg-light-grey w-full" />
        )}
      </div>

      <div className="flex justify-between">
        <div className="flex items-center gap-x-0.6 mb-0.4">
          <span className="w-0.8 h-0.8 rounded-full bg-magenta inline-block" />
          <span className="text-12 text-darkest-grey">New Patients</span>
        </div>
        <div className="text-14 font-bold text-darkest-grey">
          {totalNew.toLocaleString()}{' '}
          <span className="font-normal text-12">({newPct.toFixed(1)}%)</span>
        </div>
        <PeriodComparison
          current={totalNew}
          last={lastNew}
          isVsLastPeriod={false}
        />
      </div>

      <div className="flex justify-between">
        <div className="flex items-center gap-x-0.6 mb-0.4">
          <span className="w-0.8 h-0.8 rounded-full bg-[#D8B4FE] inline-block" />
          <span className="text-12 text-darkest-grey">Existing Patients</span>
        </div>
        <div className="text-14 font-bold text-darkest-grey">
          {totalExisting.toLocaleString()}{' '}
          <span className="font-normal text-12">
            ({existingPct.toFixed(1)}%)
          </span>
        </div>
        <PeriodComparison
          current={totalExisting}
          last={lastExisting}
          isVsLastPeriod={false}
        />
      </div>
    </div>
  );
};

export default PatientsCard;
